import { Content, ContentStack, ContentTable } from "pdfmake/interfaces";
import HeaderSapiensForm from "react-lib/apps/HISV3/DPO/sequence/HeaderSapiensForm";
import { CompanyType } from "../common/CompanyLogoForm";

import CONFIG from "config/config";

const color = {
  greyHeader: "#D9D9D9",
  greySummary: "#E8E8E8",
  greyBorder: "#9f9f9f",
};

type FormSapiensReceiptProps = {
  payer?: string;
  payer_tax_id?: string;
  payer_address?: string;
  name?: any;
  time?: any;
  code?: string;
  created?: string;
  createdTime?: string;
  hn?: string;
  patient_name?: string;
  // data
  fields?: Record<string, any>[];
  // summary
  totalNonClaimable?: any;
  totalSelfReimbursement?: any;
  totalAmount?: any;
  total_amount_text?: string;
  total_amount_textEN?: string;
  paymentTypeSapient?: string;
  patientNameEN?: string;
  price_text?: string;
  cashonly_sum?: string;
  claimable_sum?: string;
  discount_sum?: string;
  net_sum?: string;
  remark?: string;
  cashier?: string;
  isWatermark?: boolean;
  payment_type?: string;
  receiptType?: string;
  wallet_receipt_codes?: string;
  wallet_deposit_code?: string;
  cancelled_code?: string;
  encounter?: string;
  doctor?: string;
  visit_date?: string;
  coverage?: string;
  discharge_date?: string;
  division?: string;
  pageMargins?: any;
  isPayment?: boolean;
  divisionEN?: string;
  coverageEN?: string;
  priceTextEN?: string;
  total_sum?: string;
  headerNawLine?: number;
  oaDetail?: {
    name: string;
    addressTH: string;
    addressEN: string;
    taxPayerID: string;
    imageFile: any;
  };
};

type AddressLineOpts = {
  align?: "left" | "right";
  font?: string;
  bold?: boolean;
  fontSize?: number;
  style?: string;
};

const EMPTY_ADDRESS_BLOCK: Content = {
  relativePosition: { x: -10, y: 0 },
  table: {
    body: [
      [
        { text: "", border: [false, false, false, false] },
        { text: "", border: [false, false, false, false] },
      ],
    ],
  },
};

const addressLine = (text: string, opts: AddressLineOpts = {}): Content => ({
  alignment: opts.align ?? "left",
  text,
  font: opts.font ?? "KanitLM",
  lineHeight: 1.05,
  ...(opts.bold ? { bold: true } : {}),
  ...(opts.fontSize ? { fontSize: opts.fontSize } : {}),
  ...(opts.style ? { style: opts.style } : {}),
});

const buildStackAddress = (lines: Content[]): Content => ({
  relativePosition: { x: -10, y: 0 },
  stack: lines,
});

const colLine = (
  font: string,
  text: string,
  style: "hospitalName" | "hospitalInfo" = "hospitalInfo"
): Content => ({ font, style, text });

const buildTwoColumnAddress = (
  thStack: Content[],
  enStack: Content[]
): Content => ({
  relativePosition: { x: -10, y: 0 },
  table: {
    body: [
      [
        {
          lineHeight: 1,
          alignment: "left",
          border: [false, false, true, false],
          stack: thStack,
        },
        {
          lineHeight: 1.05,
          alignment: "left",
          margin: [5, 0, 0, 0],
          border: [true, false, false, false],
          stack: enStack,
        },
      ],
    ],
  },
});

const FormSapiensReceipt = async (props: FormSapiensReceiptProps) => {
  var origin = window?.location?.origin;

  const formatPrice = (number: number) => {
    return Number(number).toLocaleString("en-US", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const getHospitalAddress = (company: CompanyType): Content => {
    if (CONFIG.HIDE_COMPANY_ADDRESS_PRINT) {
      return EMPTY_ADDRESS_BLOCK;
    }

    switch (company) {
      case "MSK":
        return buildStackAddress([
          addressLine("โรงพยาบาลเอ็มเอสเค | MSK Hospital", {
            align: "right",
            fontSize: 10,
            bold: true,
          }),
          addressLine(
            "เลขที่ 118 ถ. สาทรเหนือ แขวงสีลม เขตบางรัก กรุงเทพมหานคร 10500",
            { align: "right", style: "hospitalInfo" }
          ),
          addressLine("118, Sathon Nuea Rd., Silom, Bang Rak, Bangkok 10500", {
            align: "right",
            style: "hospitalInfo",
          }),
          addressLine("T +66 2250 9535 | F +66 2250 9565", {
            align: "right",
            style: "hospitalInfo",
          }),
        ]);

      case "RAMASRI":
        return buildStackAddress([
          addressLine("ศูนย์การแพทย์รามาธิบดีศรีอยุธยา มูลนิธิรามาธิบดีฯ", {
            fontSize: 10,
            bold: true,
          }),
          addressLine(
            "Ramathibodi Sri Ayutthaya Medical Center, Ramathibodi Foundation.",
            { bold: true, style: "hospitalInfo" }
          ),
          addressLine(
            "เลขที่ : 404 ถนนศรีอยุธยา แขวงถนนพญาไท เขตราชเทวี กรุงเทพมหานคร 10400",
            { style: "hospitalInfo" }
          ),
          addressLine(
            "404 Si Ayutthaya Road, Khwaeng Thanon Phaya Thai, Khet Ratchathewi, Bangkok 10400",
            { style: "hospitalInfo" }
          ),
        ]);

      case "SAPIENS":
        return buildTwoColumnAddress(
          [
            colLine("KanitLM", "โรงพยาบาลเซเปี้ยนซ์", "hospitalName"),
            colLine("KanitLM", "บริษัท เซเปี้ยนซ์ เมดิเคชั่น จำกัด"),
            colLine("KanitLM", "สำนักงานใหญ่ 498/7 ซอยรามคำแหง 39 (เทพลีลา 1)"),
            colLine("KanitLM", "แขวงวังทองหลาง เขตวังทองหลาง"),
            colLine("KanitLM", "กรุงเทพมหานคร 10310"),
            colLine("KanitLM", "โทร +(662) 111-3703"),
            colLine("KanitLM", "เลขประจำตัวผู้เสียภาษี 0105563159409"),
          ],
          [
            colLine("Poppins", "Sapiens Pain Hospital", "hospitalName"),
            colLine("Poppins", "Sapiens Medication Co.,Ltd."),
            colLine("Poppins", "Head Office 498/7 Soi Ramkhamhaeng 39 (Thepleela 1)"),
            colLine("Poppins", "Wang Thonglang, Wang Thonglang,"),
            colLine("Poppins", "Bangkok 10310"),
            colLine("Poppins", "Tel +(662) 111-3703"),
            colLine("Poppins", "Tax ID No. 0105563159409"),
          ]
        );

      case "MEHEALTH":
        return buildTwoColumnAddress(
          [
            colLine("KanitLM", props.oaDetail?.name || "", "hospitalName"),
            colLine("KanitLM", props.oaDetail?.addressTH || ""),
            colLine(
              "KanitLM",
              `เลขประจำตัวผู้เสียภาษี ${props.oaDetail?.taxPayerID || ""}`
            ),
          ],
          [
            colLine("Poppins", props.oaDetail?.name || "", "hospitalName"),
            colLine("Poppins", props.oaDetail?.addressEN || ""),
            colLine("Poppins", `Tax ID No.  ${props.oaDetail?.taxPayerID || ""}`),
          ]
        );

      default:
        return EMPTY_ADDRESS_BLOCK;
    }
  };

  const headerSapiensForm = await HeaderSapiensForm({
    form: "FormReceipt",
    images: {
      oaLogo: props.oaDetail?.imageFile || "",
    },
    logoHight: CONFIG.COMPANY === "MSK" ? 40 : CONFIG.COMPANY === "RAMASRI" ? 45 : CONFIG.COMPANY === "MEHEALTH" ? 45 : 24,
    pageMargins: props?.pageMargins || [0, 284, 0, 225],
    hospitalAddress: getHospitalAddress(CONFIG.COMPANY),
    header: {
      margin: [-8, 0, 0, 0],
      stack: [
        {
          margin: [0, 15, 0, 0],
          table: {
            widths: [345, "*"],
            body: [
              [
                {
                  text: [
                    {
                      text: "ใบแจ้งหนี้ค่ารักษาพยาบาล",
                      font: "KanitLM",
                      style: {
                        bold: true,
                        fontSize: 10,
                      },
                    },
                    {
                      text: "(INVOICE)",
                      font: "PoppinsLM",
                      style: {
                        bold: true,
                        fontSize: 10,
                      },
                    },
                  ],
                  alignment: "right",
                  border: [false, false, false, false],
                },
                {
                  stack: [
                    {
                      text: props.isWatermark
                        ? [
                            {
                              text: "สำเนา",
                              font: "KanitLM",
                              style: "receiptHeader",
                            },
                            {
                              text: "/Copy",
                              font: "PoppinsLM",
                              style: "receiptHeader",
                            },
                          ]
                        : [
                            {
                              text: "ต้นฉบับ",
                              font: "KanitLM",
                              style: "receiptHeader",
                            },
                            {
                              text: "/Original",
                              font: "PoppinsLM",
                              style: "receiptHeader",
                            },
                          ],
                    },
                    {
                      text: [
                        {
                          text: "เลขที่",
                          font: "KanitLM",
                          style: "receiptHeader",
                        },
                        {
                          text: "/Invoice No.  ",
                          font: "PoppinsLM",
                          style: "receiptHeader",
                        },
                        {
                          text:
                            props.receiptType === "deposit"
                              ? props?.wallet_deposit_code
                              : props.receiptType === "refund"
                              ? props.cancelled_code
                              : props.code,
                          font: "PoppinsLM",
                          style: "receiptHeaderNumber",
                        },
                      ],
                    },
                  ],
                  alignment: "right",
                  border: [false, false, false, false],
                  margin: [0, 3, 20, 0],
                },
              ],
              [
                { text: "", border: [false, false, false, false] },
                { text: "", border: [false, false, false, false] },
              ],
              [
                { text: "", border: [false, false, false, false] },
                { text: "", border: [false, false, false, false] },
              ],
            ],
          },
        },
        {
          table: {
            widths: [105, 220, 65, "*"],
            body: [
              [
                {
                  text: [
                    {
                      text: "ชื่อ ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Patient Name : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: [
                    {
                      text: `${props.name}  ${props.patientNameEN || ""}`,
                      font: "KanitLM",
                      style: "receiptInfo",
                    },
                    {
                      text: "",
                      font: "PoppinsLM",
                      style: "receiptInfo",
                    },
                  ],
                  alignment: "left",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                },
                {
                  text: [
                    {
                      text: "วันที่ ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Date :",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  columns: [
                    {
                      text: props.created,
                      font: "KanitLM",
                      style: "receiptInfo",
                      width: "auto",
                    },
                    {
                      text: (props.time && `${props.time} น.`) || "",
                      font: "KanitLM",
                      style: "receiptInfo",
                      width: "*",
                      margin: [10, 0, 0, 0],
                    },
                  ],
                  alignment: "left",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                },
              ],
              [
                {
                  text: [
                    {
                      text: "เลขประจำตัวผู้ป่วย ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ HN : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: [
                    {
                      text: props.hn,
                      font: "PoppinsLM",
                      style: "receiptInfo",
                    },
                  ],
                  alignment: "left",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                },
                {
                  text: [
                    {
                      text: "EN ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ AN :",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: [
                    {
                      text: props.encounter,
                      font: "KanitLM",
                      style: "receiptInfo",
                    },
                  ],
                  alignment: "left",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                },
              ],
            ],
          },
        },
        {
          table: {
            widths: [106, 219, 135, "*"],
            body: [
              [
                {
                  text: [
                    {
                      text: "แพทย์ ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Doctor Name : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: `${props.doctor}`,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                },
                {
                  text: [
                    {
                      text: "วันที่เข้ารับการรักษา ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Visit Date :",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: props.visit_date,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                },
              ],
              [
                {
                  text: [
                    {
                      text: "สิทธิรักษา ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Coverage : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: props.coverageEN
                    ? `${props.coverage} (${props.coverageEN})`
                    : `${props.coverage}`,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                },
                {
                  text: [
                    {
                      text: "วันที่จำหน่าย ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Discharge Date :",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: props.discharge_date,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                },
              ],
            ],
          },
        },
        {
          table: {
            widths: [95, 125, 96, 85, "*"],
            body: [
              [
                {
                  text: [
                    {
                      text: "คู่สัญญา ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Contact : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: props.payer || `-`,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                },
                {
                  text: [
                    {
                      text: "Tax ID : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                    {
                      text: props.payer_tax_id || `-`,
                      font: "KanitLM",
                      style: "receiptInfo",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                },
                {
                  text: [
                    {
                      text: "แผนก ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Division : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: props.divisionEN
                    ? `${props.division} (${props.divisionEN})`
                    : `${props.division}`,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                },
              ],
            ],
          },
        },
        {
          table: {
            widths: ["auto", "*"],
            body: [
              [
                {
                  text: [
                    {
                      text: "ที่อยู่ ",
                      font: "KanitLM",
                      style: "receiptHeader",
                    },
                    {
                      text: "/ Address : ",
                      font: "PoppinsLM",
                      style: "receiptHeader",
                    },
                  ],
                  alignment: "letf",
                  border: [false, false, false, false],
                  margin: [20, 0, 0, 0],
                },
                {
                  text: props.payer_address || `-`,
                  font: "KanitLM",
                  style: "receiptInfo",
                  alignment: "left",
                  border: [false, false, false, false],
                },
              ],
            ],
          },
        },
      ],
    },
    styles: {
      receiptHeader: {
        bold: true,
        fontSize: 8,
      },
      receiptInfo: {
        fontSize: 8,
      },
      receiptHeaderNumber: {
        bold: true,
        fontSize: 8,
        color: "#095e9d",
      },
    },
  });

  let fieldsLength =
    (props.fields || []).length > 7 ? (props.fields || []).length % 7 : (props.fields || []).length;
  let numberRows = fieldsLength !== 0 ? 7 - fieldsLength : 0;

  const rowEmpty = Array.from({ length: numberRows }).map((item: any, index: number) => [
    {
      stack: [
        {
          text: " ",
          font: "KanitLM",
          style: "tableData",
        },
        {
          text: " ",
          font: "PoppinsLM",
          style: "tableData",
        },
      ],
      margin: [10, 0, 0, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
  ]);

  const fileData = (props.fields || []).map((item: any, index: number) => {
    let bilModeName = item.bill_mode__name.split(" (");
    let bilModeNameEn = [""];
    if (bilModeName?.[1].split(")")) {
      bilModeNameEn = bilModeName?.[1].split(")");
    }

    return [
      {
        // pageBreak: (index + 1) % 7 === 0 && 'after',
        stack: [
          {
            text: bilModeName?.[0] || "",
            font: "KanitLM",
            style: "tableData",
          },
          {
            text: bilModeNameEn?.[0] ? `(${bilModeNameEn?.[0]})` || "\n" : "",
            font: "PoppinsLM",
            style: "tableData",
          },
        ],
        margin: [10, 0, 0, 5],
        border: [true, false, true, false],
      },
      {
        stack: [
          {
            text: item.total__sum || "",
            font: "KanitLM",
            style: "tableData",
          },
          {
            text: "\n",
            font: "PoppinsLM",
            style: "tableData",
          },
        ],
        font: "Poppins",
        style: "tableData",
        alignment: "right",
        margin: [0, 0, 3, 5],
        border: [true, false, true, false],
      },
      {
        stack: [
          {
            text: item?.discount__sum,
            font: "KanitLM",
            style: "tableData",
          },
          {
            text: "\n",
            font: "PoppinsLM",
            style: "tableData",
          },
        ],
        font: "Poppins",
        style: "tableData",
        alignment: "right",
        margin: [0, 0, 3, 5],
        border: [true, false, true, false],
      },
      {
        stack: [
          {
            text: item?.net__sum,
            font: "KanitLM",
            style: "tableData",
          },
          {
            text: "\n",
            font: "PoppinsLM",
            style: "tableData",
          },
        ],
        font: "Poppins",
        style: "tableData",
        alignment: "right",
        margin: [0, 0, 3, 5],
        border: [true, false, true, false],
      },
    ];
  });

  const remarkSection = () => {
    const remarkTextTH =
      "*ขอสงวนสิทธิ์ไม่รับเปลี่ยนหรือคืนยาและเวชภัณฑ์เพื่อความปลอดภัยของผู้ป่วย/";
    const remarkTextEN =
      "*For patient safety, the hospital does not accept returns or exchanges of medications and medical supplies.";

    let remarkPrefix = "";
    const headerNawLine = props.headerNawLine ?? 0;

    if (headerNawLine === 0) {
      remarkPrefix = "\n\n\n";
    } else if (headerNawLine === 12) {
      remarkPrefix = "\n\n";
    } else if (headerNawLine > 24) {
      remarkPrefix = "\n";
    }

    return [
      {
        stack: [
          {
            text: remarkPrefix + remarkTextTH,
            font: "KanitLM",
            style: "footerItalics",
          },
          {
            text: remarkTextEN,
            font: "PoppinsLM",
            style: "footerItalics",
          },
        ],
        margin: [0, 5, 0, 0],
        border: [false, false, false, false],
        colSpan: 2,
      },
      {},
    ];
  };

  return {
    pageSize: "A4",
    ...headerSapiensForm,
    watermark: {
      text: props.isWatermark ? "สำเนา/ Copy" : "",
      font: "KanitLM",
      opacity: 0.2,
      bold: true,
      angle: -15,
      fontSize: 70,
    },
    background: [
      props.isPayment && !CONFIG.HIDE_COMPANY_LOGO_PRINT && headerSapiensForm.images?.logo
        ? {
            image: "logo",
            opacity: 0.18,
            width: 400,
            alignment: "center",
            absolutePosition: { y: 400 },
          }
        : {},
    ],
    content: [
      {
        margin: [7, 0, 7, 0],
        stack: [
          //data
          {
            table: {
              headerRows: 2,
              widths: [315, "*", "*", "*"],
              body: [
                [
                  {
                    text: [
                      {
                        text: "รายการ",
                        font: "KanitLM",
                        style: "receiptHeader",
                      },
                      {
                        text: "/Description",
                        font: "PoppinsLM",
                        style: "receiptHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    position: "absolute",
                    relativePosition: { y: 14 },
                    rowSpan: 2,
                  },
                  {
                    text: [
                      {
                        text: "จำนวนเงิน(บาท) ",
                        font: "KanitLM",
                        style: "receiptHeader",
                      },
                      {
                        text: "/Amount (Baht)",
                        font: "PoppinsLM",
                        style: "receiptHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    colSpan: 3,
                  },
                  {},
                  {},
                ],
                [
                  {},
                  {
                    stack: [
                      {
                        text: "จำนวนเงิน",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: "(Total Amount)",
                        font: "Poppins",
                        style: "tableData",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                  {
                    stack: [
                      {
                        text: "ส่วนลด",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: "(Discount)",
                        font: "Poppins",
                        style: "tableData",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                  {
                    stack: [
                      {
                        text: "จำนวนเงินสุทธิ",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: "(Net Amount)",
                        font: "Poppins",
                        style: "tableData",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                ],
                ...fileData,
                ...rowEmpty,
              ],
            },
            layout: {
              hLineColor: function (i: any, node: any) {
                return color.greyBorder;
              },
              vLineColor: function (i: any, node: any) {
                return color.greyBorder;
              },
            },
          },
        ],
      },
    ],
    styles: {
      ...headerSapiensForm.styles,
      tableData: { fontSize: 8 },
      tableHeaderSummary: {
        bold: true,
        fontSize: 8,
      },
      footerSignature: { fontSize: 9 },
      footerData: { bold: true, fontSize: 6 },
      footerItalics: { fontSize: 8, italics: true },
    },
    footer: function (currentPage: number, pageCount: number) {
      return {
        margin: [7, 0, 7, 0],
        stack: [
          {
            table: {
              widths: [315, "*", "*", "*"],
              body: [
                [
                  {
                    text: [
                      {
                        text: "รวมทั้งสิ้น ",
                        font: "KanitLM",
                        style: "tableHeaderSummary",
                      },
                      {
                        text: "/Total",
                        font: "PoppinsLM",
                        style: "tableHeaderSummary",
                      },
                    ],
                    margin: [10, 5, 0, 5],
                    border: [true, true, false, false],
                  },
                  {
                    text: props.total_sum || "0.00",
                    font: "Poppins",
                    style: "tableData",
                    alignment: "right",
                    margin: [0, 5, 3, 5],
                    border: [false, true, false, false],
                  },
                  {
                    text: props.discount_sum || "0.00",
                    font: "Poppins",
                    style: "tableData",
                    alignment: "right",
                    margin: [0, 5, 3, 5],
                    border: [false, true, false, false],
                  },
                  {
                    text: props.net_sum || "0.00",
                    font: "Poppins",
                    style: "tableData",
                    alignment: "right",
                    margin: [0, 5, 3, 5],
                    border: [false, true, true, false],
                  },
                ],
              ],
            },
            layout: {
              hLineColor: function (i: any, node: any) {
                return color.greyBorder;
              },
              vLineColor: function (i: any, node: any) {
                return color.greyBorder;
              },
            },
          },
          {
            table: {
              widths: [140, "*", 10],
              body: [
                [
                  {
                    text: "",
                    margin: [10, 0, 0, 0],
                    border: [true, false, false, false],
                  },
                  {
                    stack: [
                      {
                        text: `${props.price_text} (${props.priceTextEN})`,
                        font: "KanitLM",
                        style: "tableData",
                      },
                    ],
                    margin: [5, 0, 0, 0],
                    border: [false, false, false, false],
                    fillColor: color.greySummary,
                  },
                  {
                    text: "",
                    border: [false, false, true, false],
                  },
                ],
                [
                  {
                    text:
                      props.receiptType === "receipt" || props.receiptType === "deposit"
                        ? [
                            {
                              text: "ชำระเงินโดย ",
                              font: "KanitLM",
                              style: "tableHeaderSummary",
                            },
                            {
                              text: "/Payment",
                              font: "PoppinsLM",
                              style: "tableHeaderSummary",
                            },
                          ]
                        : "\n",
                    margin: [10, 0, 0, 0],
                    border: [true, false, false, true],
                  },
                  {
                    text:
                      props.receiptType === "receipt" || props.receiptType === "deposit"
                        ? props.payment_type
                        : "",
                    font: "KanitLM",
                    style: "tableData",
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: "",
                    border: [false, false, true, true],
                  },
                ],
              ],
            },
            layout: {
              hLineColor: function (i: any, node: any) {
                return color.greyBorder;
              },
              vLineColor: function (i: any, node: any) {
                return color.greyBorder;
              },
            },
          },
          props.coverage !== "เงินสด"
            ? {
                stack: [
                  {
                    text: "กรณีที่บริษัทคู่สัญญาปฏิเสธการจ่าย ข้าพเจ้าตกลงชำระเงินทั้งสิ้นตามยอดค่าใช้จ่ายจริง / In case the contracted company refuses to pay, I agree to pay the total actual",
                  },
                  {
                    columns: [
                      {
                        width: "auto",
                        text: "expenses of",
                      },
                      {
                        width: 60,
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 10,
                            x2: 60,
                            y2: 10,
                            lineWidth: 1,
                          },
                        ],
                      },
                      {
                        width: "auto",
                        text: " บาท/Baht ให้แก่โรงพยาบาล / to the hospital และยินยอมให้โรงพยาบาลเปิดเผยประวัติทางการแพทย์ของข้าพเจ้าต่อบริษัทคู่สัญญา / and consent",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        width: "auto",
                        text: "to the hospital disclosing my medical records to the contracted company ส่วนเกินสิทธื์/ Excess amount",
                      },
                      {
                        width: 60,
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 10,
                            x2: 60,
                            y2: 10,
                            lineWidth: 1,
                          },
                        ],
                      },
                      {
                        width: "auto",
                        text: " บาท/Baht",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        width: "auto",
                        text: "ใบเสร็จรับเงินเลขที่ / Receipt No",
                      },
                      {
                        width: 60,
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 10,
                            x2: 60,
                            y2: 10,
                            lineWidth: 1,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    text: `\nโปรดเรียกเก็บใบเสร็จรับเงินเมื่อเวลาชำระเงินตามบิลนี้/ Please request the receipt upon payment of this bill.`,
                  },
                ],
                font: "KanitLM",
                style: "tableData",
                margin: [5, 5, 5, 10],
              }
            : {
                text: "\n\n\n\n\n",
                font: "KanitLM",
                style: "tableData",
                margin: [5, 5, 5, 10],
              },
          {
            margin: [0, 0, 0, 0],
            table: {
              widths: ["*", 270],
              body: [
                // [
                //   {
                //     text: "\n",
                //     border: [false, false, false, false],
                //     colSpan: 2,
                //   },
                //   {},
                // ],
                [
                  {
                    stack: [
                      {
                        text: "ลงชื่อ/Signature ____________________ผู้ป่วย/Patient ",
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "left",
                        margin: [35, 0, 0, 0],
                      },
                      {
                        text: `(          ${props.name}        )`,
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                    ],
                    border: [false, false, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ลงชื่อ/Signature ____________________เจ้าหน้าที่การเงิน/Cashier",
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                      {
                        text: `(          ${props.cashier}        )`,
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                        margin: [0, 0, 35, 0],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
                remarkSection(),
              ],
            },
          },
        ],
      };
    },
    images: {
      ...headerSapiensForm.images,
      background: origin + "/static/images/logoform/SAPIENS/HeaderSapiensForm.png",
    },
  };
};

export default FormSapiensReceipt;
